import React, { useEffect } from "react";
// @ts-ignore
import Importer from "@tinyemail/tiny-csv-importer";
import { Dialog, DialogTitle, DialogContent } from "@tiny/components";
import { useTheme } from "@mui/material";
import { TinySuppressionImporterProps } from "./TinySuppressionImporter.types";
import useTinyImporter from "./TinySuppressionImporter.hooks";
import {
  useAppendSuppressionMutation,
  usePutSuppressionDetailsMutation,
  usePutSuppressionMutation,
} from "@tiny/api";
import { extractErrorMessage } from "@tiny/components/src/utils/errorUtils";
import { v4 as uuid } from "uuid";
import { CreateSuppressionRequestView } from "@tiny/api";
import { showUploadMessage } from "@tiny/components/src/Snackbar/snackbar-helpers";
import { closeSnackbar } from "notistack";
import { useTinyHost } from "@tiny/host-contract";

declare global {
  interface Window {
    dataLayer: any;
  }
}

function TinySuppressionImporter(props: TinySuppressionImporterProps) {
  const {
    open,
    onClose,
    onSuccess,
    suppressionDetails,
    suppressionId,
    suppressionType,
    mode = "create",
  } = props;
  const { navigate } = useTinyHost();
  const muiTheme = useTheme();
  // Match the CSV importer palette to the host brand (see TinyImporter).
  const importerTheme = React.useMemo(
    () => ({
      colors: {
        primary: muiTheme.palette.primary.main,
        secondary: muiTheme.palette.primary.main,
        success: muiTheme.palette.primary.main,
        danger: muiTheme.palette.error.main,
        warning: muiTheme.palette.warning.main,
      },
    }),
    [muiTheme]
  );
  const { fields } = useTinyImporter(suppressionType || "EMAIL");

  const { mutateAsync: putSuppressionMutateAsync, isLoading: saving } =
    usePutSuppressionMutation({
      onError(e) {
        extractErrorMessage(extractErrorMessage(e));
      },
      onSuccess() {
        console.log("successfully uploaded");
      },
    });

  const { mutateAsync: putSuppressionDetailsMutateAsync } =
    usePutSuppressionDetailsMutation({
      onError(e) {
        extractErrorMessage(extractErrorMessage(e));
      },
      onSuccess() {
        console.log("successfully uploaded");
      },
    });

  const { mutateAsync: appendSuppressionMutateAsync } =
    useAppendSuppressionMutation({
      onError(e) {
        extractErrorMessage(extractErrorMessage(e));
      },
      onSuccess() {
        console.log("successfully uploaded");
      },
    });

  const postBulkAdd = async (suppData: any) => {
    if (mode === "append") {
      const csvContent = convertJsonToCsv(suppData);
      const csvBlob = new Blob([csvContent], { type: "text/csv" });
      const csvFile = new File(
        [csvBlob],
        `suppression_list_${uuid().replace(/-/g, "")}.csv`,
        { type: "text/csv" }
      );

      showUploadMessage({
        variant: "uploading",
        message: "Uploading file",
        description: "Your suppression list is uploading",
        persist: true,
        showClose: true,
      });
      try {
        await appendSuppressionMutateAsync({
          suppressionId: suppressionId || "",
          file: csvFile,
        });
        closeSnackbar();
        onSuccess({} as CreateSuppressionRequestView);
        showUploadMessage({
          variant: "success",
          message: "Uploaded successfully!",
          description: "Your suppression list has been appended successfully!",
          primaryCtaActionText: "close",
          primaryCtaActionFunction: () => {
            closeSnackbar();
          },
          persist: true,
        });
      } catch (error) {
        closeSnackbar();
        showUploadMessage({
          variant: "failed",
          message: "Uploading error!",
          description:
            "It seems file size is large for browser. Please contact the support team.",
          showClose: true,
        });
        throw new Error();
      }
    } else if (Object.keys(suppressionDetails || {}).length) {
      const csvContent = convertJsonToCsv(suppData);
      const csvBlob = new Blob([csvContent], { type: "text/csv" });
      const csvFile = new File(
        [csvBlob],
        `suppression_list_${uuid().replace(/-/g, "")}.csv`,
        { type: "text/csv" }
      );

      const data = {
        file: csvFile,
        suppression: suppressionDetails as CreateSuppressionRequestView,
      };
      showUploadMessage({
        variant: "uploading",
        message: "Uploading file",
        description: `Your ${suppressionDetails?.name} suppression list is uploading`,
        persist: true,
        showClose: true,
      });
      try {
        const response = await putSuppressionMutateAsync({ data });
        const { id } = response;
        closeSnackbar();
        onSuccess(response);
        showUploadMessage({
          variant: "success",
          message: "Uploaded successfully!",
          description: `Your ${suppressionDetails?.name} suppression list has been uploaded successfully!`,
          primaryCtaActionText: "close",
          primaryCtaActionFunction: () => {
            closeSnackbar();
          },
          persist: true,
        });
      } catch (error) {
        closeSnackbar();
        showUploadMessage({
          variant: "failed",
          message: "Uploading error!",
          description:
            "It seems file size is large for browser. Please contact the support team.",
          showClose: true,
        });
        throw new Error();
      }
    } else {
      const data = {
        suppressionValues: suppData,
      };

      showUploadMessage({
        variant: "uploading",
        message: "Uploading file",
        description: "Your suppression list is uploading",
        persist: true,
        showClose: true,
      });
      try {
        const response = await putSuppressionDetailsMutateAsync({
          suppressionId: suppressionId || "",
          data,
        });
        const { id } = response;
        closeSnackbar();
        onSuccess(response);
        showUploadMessage({
          variant: "success",
          message: "Uploaded successfully!",
          description: "Your suppression list has been uploaded successfully!",
          primaryCtaActionText: "View",
          primaryCtaActionFunction: () => {
            navigate.to(`/main/audience/suppression/${id ?? ""}`, {
              state: {
                backUrl: {
                  pathname: location.pathname,
                  search: location.search,
                },
              },
            });
            closeSnackbar();
          },
          secondaryCtaActionText: "Cancel",
          secondaryCtaActionFunction: () => {
            closeSnackbar();
          },
          persist: true,
        });
      } catch (error) {
        closeSnackbar();
        showUploadMessage({
          variant: "failed",
          message: "Uploading error!",
          description:
            "It seems file size is large for browser. Please contact the support team.",
          showClose: true,
        });
        throw new Error();
      }
    }
  };

  const convertJsonToCsv = (jsonData: any[]): string => {
    if (jsonData.length === 0) {
      return "";
    }

    const headers = Object.keys(jsonData[0]);
    const headerRow = headers.join(",");

    const rows = jsonData.map((obj) => {
      return headers
        .map((header) => (obj[header] !== undefined ? obj[header] : ""))
        .join(",");
    });

    const result = [headerRow, ...rows].join("\n");
    return result;
  };

  const emitGTM = (state: string) => {
    // Guard: hosts without GTM (TinyAlbert) have no window.dataLayer.
    (window.dataLayer = window.dataLayer || []).push({
      event: `tiny-suppression-importer-${state}`,
    });
  };

  React.useEffect(() => {
    if (open) {
      emitGTM("open");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      disableEscapeKeyDown
      disableBackdropClose
      PaperProps={{
        style: {
          padding: 100,
        },
      }}
    >
      <DialogTitle
        onClose={() => {
          onClose();
          emitGTM("close");
        }}
      >
        {mode === "append"
          ? "Add to Suppression List"
          : "Upload Suppression List"}
      </DialogTitle>
      <DialogContent>
        <Importer
          theme={importerTheme}
          fields={fields}
          onComplete={async (data: any) => {
            try {
              postBulkAdd(data);
              onClose();
            } catch (e) {
              throw new Error();
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

export default TinySuppressionImporter;
